/* eslint class-methods-use-this: 0 */
import {
  filter, find, flatMapDeep, isEmpty, isNil, map, slice, uniq,
} from 'lodash';
import Api from 'client/api/v2/Api';
import PolicyTemplateApi from 'client/api/v2/PolicyTemplateApi';
import policyGroupTemplates from 'client/util/Constants/PolicyGroupTemplateConstants';

class DefaultPolicyGroupTemplatesApi extends Api {
  /**
   * Overriding to fetch the local/static Policy Group Templates.
   */
  fetch(skip, limit, sort, searchTerm) {
    return this.fetchDefaultPolicyGroupTemplates()
      .then((t) => {
        this.templates = t;

        // Search
        this.applySearch(searchTerm);

        // Skip/Limit
        this.applySkipLimit(skip, limit);

        // Sort
        this.templates.sort(this.sortByIndex);

        const results = {
          totalCount: t.length,
          results: this.templates,
        };

        return results;
      });
  }

  // Util methods
  getDefaultPolicyGroupTemplates() {
    return policyGroupTemplates;
  }

  async getPolicyTemplatesByName(pTemplateNames) {
    return PolicyTemplateApi
      .findPolicyTemplatesByName(pTemplateNames, { limit: pTemplateNames.length });
  }

  async fetchDefaultPolicyGroupTemplates() {
    // Get the policy group templates from our static list.
    const pgTemplates = this.getDefaultPolicyGroupTemplates();

    // Extract a unique list of base policy template names from each of the
    // group template's policies.
    const pTemplateNames = uniq(map(
      flatMapDeep(policyGroupTemplates, 'policies'),
      'templateName',
    ));

    // With the names, get a list of specific policy templates from the API.
    const pTemplates = await this.getPolicyTemplatesByName(pTemplateNames);

    // With the templates now available, go over each policy group template's
    // policies and add the policy template to the policy. This is required by
    // the UI in several locations.
    pgTemplates.forEach((group, groupKey) => {
      group.policies.forEach((policy, policyKey) => {
        const template = find(pTemplates.results, { name: policy.templateName });

        // We require the template  If the template is missing, don't update the
        // policy. It'll get removed in the next step.
        if (!template) {
          return;
        }

        // Update the existing policy with its template counterpart.
        pgTemplates[groupKey].policies[policyKey] = {
          ...policy,
          template,
        };
      });
    });

    // Remove policies that don't have a template
    pgTemplates.forEach((group) => {
      group.policies.forEach((policy, policyKey) => {
        if (!policy.template) {
          group.policies.splice(policyKey, 1);
        }
      });
    });

    // Return only groups that have policies
    return filter(pgTemplates, g => g.policies.length > 0);
  }

  applySearch(searchTerm) {
    if (isEmpty(searchTerm)) return;

    this.templates = filter(this.templates, (group) => group.name.toLowerCase()
      .includes(searchTerm.toLowerCase()));
  }

  applySkipLimit(skip, limit) {
    if (isNil(skip) && isNil(limit)) return;

    const skipValue = isNil(skip) ? 0 : skip;
    const limitValue = isNil(limit) ? undefined : skipValue + limit;
    this.templates = slice(this.templates, skipValue, limitValue);
  }

  sortByIndex(a, b) {
    const itemIndex = (item) => policyGroupTemplates
      .findIndex(pgt => pgt.name === item.name);
    return itemIndex(a) - itemIndex(b);
  }
}

export default new DefaultPolicyGroupTemplatesApi();
